import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const SaleContactForm = ({ campervan }) => {
  const { t } = useTranslation("common");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    interestedIn: "purchase",
    budget: "",
    timeline: "",
    financingNeeded: "no",
    currentVehicle: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In real app, this would send data to backend
    console.log("Contact form submitted:", formData);
    alert("Thank you for your interest! We'll contact you within 24 hours.");
  };

  return (
    <div className="sale-contact-form">
      <div className="contact-form-header">
        <h3>Interested in this Campervan?</h3>
        <p>
          Fill out the form below and our sales team will contact you within 24
          hours.
        </p>
      </div>

      <div className="vehicle-summary">
        <img
          src={campervan.mainImage}
          alt={campervan.title}
          className="summary-image"
        />
        <div className="summary-details">
          <h4>{campervan.title}</h4>
          <p className="summary-price">{campervan.price}</p>
          <span className={`summary-status ${campervan.status.toLowerCase()}`}>
            {campervan.status}
          </span>
        </div>
      </div>

      <form className="contact-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h4>Personal Information</h4>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Purchase Details</h4>
          <div className="form-group">
            <label htmlFor="interestedIn">I'm interested in *</label>
            <select
              id="interestedIn"
              name="interestedIn"
              value={formData.interestedIn}
              onChange={handleInputChange}
              required
            >
              <option value="purchase">Purchasing this campervan</option>
              <option value="more-info">Getting more information</option>
              <option value="viewing">Scheduling a viewing</option>
              <option value="financing">Learning about financing</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="budget">Budget Range</label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
              >
                <option value="">Select budget range</option>
                <option value="under-30k">Under $30,000</option>
                <option value="30k-40k">$30,000 - $40,000</option>
                <option value="40k-50k">$40,000 - $50,000</option>
                <option value="over-50k">Over $50,000</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="timeline">Purchase Timeline</label>
              <select
                id="timeline"
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
              >
                <option value="">Select timeline</option>
                <option value="immediate">Ready to buy now</option>
                <option value="1-month">Within 1 month</option>
                <option value="3-months">Within 3 months</option>
                <option value="6-months">Within 6 months</option>
                <option value="just-browsing">Just browsing</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="financingNeeded">
              Do you need financing assistance?
            </label>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="financing-yes"
                  name="financingNeeded"
                  value="yes"
                  checked={formData.financingNeeded === "yes"}
                  onChange={handleInputChange}
                />
                <label htmlFor="financing-yes">Yes, I need financing</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="financing-no"
                  name="financingNeeded"
                  value="no"
                  checked={formData.financingNeeded === "no"}
                  onChange={handleInputChange}
                />
                <label htmlFor="financing-no">No, I'm paying cash</label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="currentVehicle">
              Do you have a vehicle to trade in?
            </label>
            <input
              type="text"
              id="currentVehicle"
              name="currentVehicle"
              value={formData.currentVehicle}
              onChange={handleInputChange}
              placeholder="e.g., 2020 Toyota Camry, No trade-in"
            />
          </div>
        </div>

        <div className="form-section">
          <div className="form-group">
            <label htmlFor="message">Additional Questions or Comments</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows="4"
              placeholder="Any specific questions about this campervan or additional requirements..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button">
            <span>Send Inquiry</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 8L8 4L4 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                transform="rotate(90 8 8)"
              />
            </svg>
          </button>
          <p className="form-note">
            By submitting this form, you agree to be contacted by our sales
            team. We respect your privacy and will never share your information.
          </p>
        </div>
      </form>

      <div className="contact-alternatives">
        <h4>Prefer to contact us directly?</h4>
        <div className="contact-methods">
          <div className="contact-method">
            <div className="contact-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 3H18V17H2V3Z"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 3L10 11L2 3"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <span className="contact-label">Email</span>
              <span className="contact-value">sales@suncatchers.com</span>
            </div>
          </div>
          <div className="contact-method">
            <div className="contact-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 14V17C18 17.5304 17.7893 18.0391 17.4142 18.4142C17.0391 18.7893 16.5304 19 16 19C12.4 19 9 16.5 7 13C4.5 9 2 5.6 2 2C2 1.46957 2.21071 0.960859 2.58579 0.585786C2.96086 0.210714 3.46957 0 4 0H7L9 5L7.5 7.5C8.4 9.6 10.4 11.6 12.5 12.5L15 11L20 13V16Z"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <span className="contact-label">Phone</span>
              <span className="contact-value">+351 925 025 908</span>
              <span className="contact-phone-note">{t('footer.contact.phoneNote')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleContactForm;
